<script setup lang="ts">
import { ref } from 'vue';
import { definePageMeta } from '#imports';
import { useBusinessesStore } from '~/store/businesses';
import { OnboardingIntroduction } from './_components/onboarding-introduction';
import { authRequired } from '~/middleware/auth-required';
import { OnboardingStepper } from './_components/onboarding-stepper';
import { OnboardingComplete } from './_components/onboarding-complete';
import { Footer } from '~/components/footer';

definePageMeta({
  layout: false,
  middleware: [
    authRequired,
    () => {
      const { selectedBusiness } = storeToRefs(useBusinessesStore());

      if (
        !selectedBusiness.value ||
        selectedBusiness.value.isOnboardingCompleted !== false ||
        selectedBusiness.value.isOnboardingExpired === true
      ) {
        return navigateTo('/');
      }
    },
  ],
});

const { nonNullableSelectedBusiness } = storeToRefs(useBusinessesStore());

const businessOnboarding = computed(() => {
  if (!nonNullableSelectedBusiness.value.businessOnboarding) {
    throw new Error('Business Onboarding must not be null');
  }

  return nonNullableSelectedBusiness.value.businessOnboarding;
});

const isOnboardingStartNeeded = computed<boolean>(
  () =>
    businessOnboarding.value.introductionPageName !== null &&
    nonNullableSelectedBusiness.value.onboardingStartedAt === null,
);

const INTRODUCTION_PAGE_STEP_INDEX = -1;

const nonCompletedStepIndex = businessOnboarding.value.steps.findIndex(
  (step) => !step.isCompleted,
);

const currentStepIndex = ref<number>(
  isOnboardingStartNeeded.value
    ? INTRODUCTION_PAGE_STEP_INDEX
    : nonCompletedStepIndex,
);

const isOnboardingCompleted = computed<boolean>(
  () => currentStepIndex.value >= businessOnboarding.value.steps.length,
);

const onGetStarted = () => {
  currentStepIndex.value = 0;
};
</script>

<template>
  <div class="bg-background min-h-screen">
    <OnboardingIntroduction
      v-if="
        currentStepIndex === INTRODUCTION_PAGE_STEP_INDEX &&
        businessOnboarding.introductionPageName
      "
      :business="nonNullableSelectedBusiness"
      :image-url="businessOnboarding.imageUrl"
      :introduction-page-name="businessOnboarding.introductionPageName"
      @get-started="onGetStarted"
    />

    <OnboardingStepper
      v-else-if="!isOnboardingCompleted"
      v-model="currentStepIndex"
      :business-onboarding="businessOnboarding"
    />

    <OnboardingComplete v-else :image-url="businessOnboarding.imageUrl" />

    <Footer
      :class="{
        'border-t-0':
          currentStepIndex === INTRODUCTION_PAGE_STEP_INDEX ||
          isOnboardingCompleted,
      }"
    />
  </div>
</template>
